/**
 * Abstract Base Class for Commercial Properties
 * 
 * This provides common functionality for all commercial property types
 * (Offices, Warehouses, Retail, Retail Warehouses, etc.)
 * 
 * Shared characteristics:
 * - Commercial spaces (offices, meeting rooms, warehouses)
 * - Loading docks and logistics
 * - IT infrastructure
 * - Commercial features (HVAC, fire suppression, security)
 * - Business-oriented amenities
 */

import { PropertyTypeHandler, PropertyCategory, PropertyValidationRule } from './property-type-handler.abstract';
import { FeatureConfig, RoomFormField, FeatureCategory as FeatureScopeCategory } from '@core/services/property-analysis/property-room-config.service';

export abstract class CommercialPropertyHandler extends PropertyTypeHandler {
  readonly category = PropertyCategory.Commercial;

  /**
   * Common commercial features shared across offices, warehouses, retail
   */
  protected getCoreCommercialFeatures(): FeatureConfig[] {
    return [
      {
        key: 'heating_system',
        label: 'properties.featureTypes.heating_system.label',
        icon: 'thermostat',
        category: FeatureScopeCategory.FLEXIBLE, // Can be building central OR unit-specific
        description: 'properties.featureTypes.heating_system.description',
        detailsInterface: 'HVACDetail',
        formFields: this.getHeatingSystemFormFields()
      },
      {
        key: 'cooling_system',
        label: 'properties.featureTypes.cooling_system.label',
        icon: 'ac_unit',
        category: FeatureScopeCategory.FLEXIBLE, // Can be building central OR unit-specific
        description: 'properties.featureTypes.cooling_system.description',
        detailsInterface: 'HVACDetail',
        formFields: this.getCoolingSystemFormFields()
      },
      {
        key: 'security_system',
        label: 'properties.featureTypes.security_system.label',
        icon: 'security',
        category: FeatureScopeCategory.BUILDING_ONLY, // Building access control & security
        description: 'properties.featureTypes.security_system.description',
        detailsInterface: 'SecurityDetail',
        formFields: this.getCommercialSecurityFormFields()
      },
      {
        key: 'cctv',
        label: 'properties.featureTypes.cctv.label',
        icon: 'videocam',
        category: FeatureScopeCategory.BUILDING_ONLY, // Building-level surveillance
        description: 'properties.featureTypes.cctv.description',
        detailsInterface: 'SecurityDetail',
        formFields: this.getCCTVFormFields()
      },
      {
        key: 'fire_suppression',
        label: 'properties.featureTypes.fire_suppression.label',
        icon: 'fire_extinguisher',
        category: FeatureScopeCategory.BUILDING_ONLY, // Building fire safety system
        description: 'properties.featureTypes.fire_suppression.description',
        detailsInterface: 'FireSuppressionDetail',
        formFields: this.getFireSuppressionFormFields()
      },
      {
        key: 'backup_power',
        label: 'properties.featureTypes.backup_power.label',
        icon: 'power',
        category: FeatureScopeCategory.BUILDING_ONLY, // Building-level generator/UPS
        description: 'properties.featureTypes.backup_power.description',
        detailsInterface: 'BuildingSystems',
        formFields: this.getBackupPowerFormFields()
      }
    ];
  }

  /**
   * Common validation rules for commercial properties
   */
  getValidationRules(): PropertyValidationRule[] {
    return [
      {
        fieldName: 'surfaces.netSurface',
        rules: {
          required: true,
          min: 20,
          errorMessage: 'Commercial surface area must be at least 20 m²'
        }
      },
      {
        fieldName: 'characteristics.parkingSpaces',
        rules: {
          min: 0,
          max: 500,
          errorMessage: 'Parking spaces must be between 0 and 500'
        }
      }
    ];
  }

  // ==================== FORM FIELD DEFINITIONS ====================

  protected getHeatingSystemFormFields(): RoomFormField[] {
    return [
      {
        name: 'heatingType',
        label: 'properties.fields.heatingType',
        type: 'select',
        required: true,
        options: [
          { value: 'central_gas', label: 'properties.fieldOptions.commercialHeatingType.central_gas' },
          { value: 'central_electric', label: 'properties.fieldOptions.commercialHeatingType.central_electric' },
          { value: 'heat_pump', label: 'properties.fieldOptions.commercialHeatingType.heat_pump' },
          { value: 'individual', label: 'properties.fieldOptions.commercialHeatingType.individual' },
          { value: 'underfloor', label: 'properties.fieldOptions.commercialHeatingType.underfloor' },
          { value: 'radiators', label: 'properties.fieldOptions.commercialHeatingType.radiators' }
        ]
      },
      {
        name: 'heatingCapacity',
        label: 'properties.fields.heatingCapacity',
        type: 'number',
        suffix: 'kW',
        min: 1
      },
      {
        name: 'fuelType',
        label: 'properties.fields.fuelType',
        type: 'select',
        options: [
          { value: 'natural_gas', label: 'properties.fieldOptions.fuelType.natural_gas' },
          { value: 'electricity', label: 'properties.fieldOptions.fuelType.electricity' },
          { value: 'fuel_oil', label: 'properties.fieldOptions.fuelType.fuel_oil' },
          { value: 'heat_pump', label: 'properties.fieldOptions.fuelType.heat_pump' },
          { value: 'biomass', label: 'properties.fieldOptions.fuelType.biomass' }
        ]
      },
      {
        name: 'zones',
        label: 'properties.fields.numberOfZones',
        type: 'number',
        min: 1,
        default: 1
      },
      {
        name: 'hasBackupSystem',
        label: 'properties.fields.hasBackupSystem',
        type: 'checkbox',
        default: false
      },
      {
        name: 'maintenanceDate',
        label: 'properties.fields.lastMaintenanceDate',
        type: 'date'
      },
      {
        name: 'efficiency',
        label: 'properties.fields.efficiencyRating',
        type: 'number',
        suffix: '%',
        min: 0,
        max: 100
      }
    ];
  }

  protected getCoolingSystemFormFields(): RoomFormField[] {
    return [
      {
        name: 'coolingType',
        label: 'properties.fields.coolingType',
        type: 'select',
        required: true,
        options: [
          { value: 'central_ac', label: 'properties.fieldOptions.coolingType.central_ac' },
          { value: 'vrv_vrf', label: 'properties.fieldOptions.coolingType.vrv_vrf' },
          { value: 'split_units', label: 'properties.fieldOptions.coolingType.split_units' },
          { value: 'chiller', label: 'properties.fieldOptions.coolingType.chiller' },
          { value: 'evaporative', label: 'properties.fieldOptions.coolingType.evaporative' }
        ]
      },
      {
        name: 'coolingCapacity',
        label: 'properties.fields.coolingCapacity',
        type: 'number',
        suffix: 'kW',
        min: 1
      },
      {
        name: 'zones',
        label: 'properties.fields.numberOfZones',
        type: 'number',
        min: 1,
        default: 1
      },
      {
        name: 'refrigerantType',
        label: 'properties.fields.refrigerantType',
        type: 'select',
        options: [
          { value: 'r410a', label: 'R-410A' },
          { value: 'r32', label: 'R-32' },
          { value: 'r134a', label: 'R-134a' },
          { value: 'r407c', label: 'R-407C' }
        ]
      },
      {
        name: 'hasBackupSystem',
        label: 'properties.fields.hasBackupSystem',
        type: 'checkbox',
        default: false
      },
      {
        name: 'maintenanceDate',
        label: 'properties.fields.lastMaintenanceDate',
        type: 'date'
      }
    ];
  }

  protected getCommercialSecurityFormFields(): RoomFormField[] {
    return [
      {
        name: 'hasAlarm',
        label: 'properties.fields.alarmSystem',
        type: 'checkbox',
        default: false
      },
      {
        name: 'alarmBrand',
        label: 'properties.fields.alarmBrand',
        type: 'text'
      },
      {
        name: 'hasAccessControl',
        label: 'properties.fields.accessControlSystem',
        type: 'checkbox',
        default: false
      },
      {
        name: 'accessControlType',
        label: 'properties.fields.accessControlType',
        type: 'select',
        options: [
          { value: 'keycard', label: 'properties.fieldOptions.accessControlType.keycard' },
          { value: 'biometric', label: 'properties.fieldOptions.accessControlType.biometric' },
          { value: 'pin_code', label: 'properties.fieldOptions.accessControlType.pin_code' },
          { value: 'rfid', label: 'properties.fieldOptions.accessControlType.rfid' },
          { value: 'mobile_app', label: 'properties.fieldOptions.accessControlType.mobile_app' }
        ]
      },
      {
        name: 'hasPerimeterSecurity',
        label: 'properties.fields.perimeterSecurity',
        type: 'checkbox',
        default: false
      },
      {
        name: 'hasSecurityGuards',
        label: 'properties.fields.securityGuards',
        type: 'checkbox',
        default: false
      },
      {
        name: 'securityGuardHours',
        label: 'properties.fields.securityGuardHours',
        type: 'select',
        options: [
          { value: '24_7', label: 'properties.fieldOptions.serviceHours.24_7' },
          { value: 'business_hours', label: 'properties.fieldOptions.serviceHours.business_hours' },
          { value: 'night_only', label: 'properties.fieldOptions.serviceHours.night_only' }
        ]
      },
      {
        name: 'hasIntrusionDetection',
        label: 'properties.fields.intrusionDetection',
        type: 'checkbox',
        default: false
      }
    ];
  }

  protected getCCTVFormFields(): RoomFormField[] {
    return [
      {
        name: 'numberOfCameras',
        label: 'properties.fields.numberOfCameras',
        type: 'number',
        required: true,
        min: 1
      },
      {
        name: 'cameraType',
        label: 'properties.fields.cameraType',
        type: 'select',
        options: [
          { value: 'analog', label: 'properties.fieldOptions.cameraType.analog' },
          { value: 'ip', label: 'properties.fieldOptions.cameraType.ip' },
          { value: 'ptz', label: 'properties.fieldOptions.cameraType.ptz' },
          { value: 'thermal', label: 'properties.fieldOptions.cameraType.thermal' }
        ]
      },
      {
        name: 'hasNightVision',
        label: 'properties.fields.nightVision',
        type: 'checkbox',
        default: false
      },
      {
        name: 'hasRemoteAccess',
        label: 'properties.fields.remoteAccess',
        type: 'checkbox',
        default: false
      },
      {
        name: 'storageType',
        label: 'properties.fields.storageType',
        type: 'select',
        options: [
          { value: 'dvr', label: 'properties.fieldOptions.cctvStorageType.dvr' },
          { value: 'nvr', label: 'properties.fieldOptions.cctvStorageType.nvr' },
          { value: 'cloud', label: 'properties.fieldOptions.cctvStorageType.cloud' },
          { value: 'hybrid', label: 'properties.fieldOptions.cctvStorageType.hybrid' }
        ]
      },
      {
        name: 'storageDuration',
        label: 'properties.fields.storageDuration',
        type: 'number',
        suffix: 'days',
        min: 1
      },
      {
        name: 'hasMotionDetection',
        label: 'properties.fields.motionDetection',
        type: 'checkbox',
        default: false
      },
      {
        name: 'hasAlerts',
        label: 'properties.fields.alertNotifications',
        type: 'checkbox',
        default: false
      }
    ];
  }

  protected getFireSuppressionFormFields(): RoomFormField[] {
    return [
      {
        name: 'systemType',
        label: 'properties.fields.systemType',
        type: 'select',
        required: true,
        options: [
          { value: 'sprinkler', label: 'properties.fieldOptions.fireSuppressionType.sprinkler' },
          { value: 'foam', label: 'properties.fieldOptions.fireSuppressionType.foam' },
          { value: 'gas', label: 'properties.fieldOptions.fireSuppressionType.gas' },
          { value: 'water_mist', label: 'properties.fieldOptions.fireSuppressionType.water_mist' },
          { value: 'dry_chemical', label: 'properties.fieldOptions.fireSuppressionType.dry_chemical' }
        ]
      },
      {
        name: 'coverage',
        label: 'properties.fields.coverageArea',
        type: 'number',
        suffix: 'm²',
        min: 1
      },
      {
        name: 'installationDate',
        label: 'properties.fields.installationDate',
        type: 'date'
      },
      {
        name: 'lastInspectionDate',
        label: 'properties.fields.lastInspectionDate',
        type: 'date'
      },
      {
        name: 'maintenanceCompany',
        label: 'properties.fields.maintenanceCompany',
        type: 'text'
      },
      {
        name: 'hasAlarmConnection',
        label: 'properties.fields.connectedToFireAlarm',
        type: 'checkbox',
        default: true
      },
      {
        name: 'hasEmergencyShutoff',
        label: 'properties.fields.emergencyShutoff',
        type: 'checkbox',
        default: false
      }
    ];
  }

  protected getBackupPowerFormFields(): RoomFormField[] {
    return [
      {
        name: 'systemType',
        label: 'properties.fields.systemType',
        type: 'select',
        required: true,
        options: [
          { value: 'generator', label: 'properties.fieldOptions.backupPowerType.generator' },
          { value: 'generator_gas', label: 'properties.fieldOptions.backupPowerType.generator_gas' },
          { value: 'ups', label: 'properties.fieldOptions.backupPowerType.ups' },
          { value: 'battery', label: 'properties.fieldOptions.backupPowerType.battery' },
          { value: 'hybrid', label: 'properties.fieldOptions.backupPowerType.hybrid' }
        ]
      },
      {
        name: 'capacity',
        label: 'properties.fields.capacity',
        type: 'number',
        suffix: 'kVA',
        min: 1
      },
      {
        name: 'runtime',
        label: 'properties.fields.runtime',
        type: 'number',
        suffix: 'hours',
        min: 1
      },
      {
        name: 'hasAutomaticSwitching',
        label: 'properties.fields.automaticTransferSwitch',
        type: 'checkbox',
        default: false
      },
      {
        name: 'maintenanceDate',
        label: 'properties.fields.lastMaintenanceDate',
        type: 'date'
      },
      {
        name: 'lastTested',
        label: 'properties.fields.lastTestDate',
        type: 'date'
      },
      {
        name: 'fuelCapacity',
        label: 'properties.fields.fuelCapacity',
        type: 'number',
        suffix: 'liters'
      }
    ];
  }
}

